/**
 * Iter60.p55b — Global Incident Directory (HR + Admin only).
 *
 * Route: `/incidents` (top-level, not under `/m/`).
 *
 * Reuses the visual language of the module-scoped `Incidents.jsx`
 * page but adds a `Module` column with a code chip and a module
 * multi-select filter. The detail drawer, create form, and cross-link
 * chip are the same components imported from `Incidents.jsx`. Access
 * is enforced twice: (a) frontend `ProtectedRoute` gate + (b) backend
 * RBAC (Manager sees only their scope; Officer 403s at the API).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";
import useModules from "../hooks/useModules";

import AppShell from "../components/AppShell";
import ModuleIdChip from "../components/ModuleIdChip";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";

// Import the same detail drawer + create dialog + labels used by the
// module-scoped page — DRY.
import {
    TYPE_LABELS,
    SEVERITY_TONE,
    STATUS_TONE,
    STATUSES,
    SEVERITIES,
    CreateIncidentDialog,
    DetailDrawer,
    fmtDate,
} from "./Incidents";

const NAV_ROLES = [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER];

function ModuleCodeChip({ code }) {
    const upper = (code || "").toUpperCase() || "—";
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide mono"
            style={{
                background: "rgba(27, 42, 86, 0.10)",
                color: "var(--asl-navy)",
                border: "1px solid rgba(27, 42, 86, 0.18)",
            }}
            data-testid={`global-incidents-module-chip-${(code || 'none').toLowerCase()}`}
        >
            {upper}
        </span>
    );
}

export default function IncidentsGlobal() {
    return (
        <AppShell title="Incidents & Emergencies · Global">
            <Body />
        </AppShell>
    );
}

function Body() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { modules } = useModules();
    const [searchParams, setSearchParams] = useSearchParams();

    const allowed = user && NAV_ROLES.includes(user.role);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(() => ({
        module_code: "any",
        status: searchParams.get("status") || "any",
        severity: "any",
        incident_type: "any",
        red_flag_only: false,
        q: "",
    }));
    const [createOpen, setCreateOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [createModuleId, setCreateModuleId] = useState(null);

    // Iter60.p60 Part A — Admin-only bulk & individual delete for the
    // global directory. Same UX as the module-scoped page.
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const toggleSelectAll = useCallback((all) => {
        setSelectedIds(() => (all ? new Set(items.map((i) => i.id)) : new Set()));
    }, [items]);

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (filters.module_code !== "any") params.module_code = filters.module_code;
            if (filters.status !== "any") params.status = filters.status;
            if (filters.severity !== "any") params.severity = filters.severity;
            if (filters.incident_type !== "any") params.incident_type = filters.incident_type;
            if (filters.red_flag_only) params.red_flag_only = true;
            if (filters.q.trim()) params.q = filters.q.trim();
            const res = await api.get("/incidents", { params });
            setItems(res.data?.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [allowed, filters]);

    useEffect(() => { load(); }, [load]);

    // Iter60.p60 Part A — Drop selections that fell off screen.
    useEffect(() => {
        setSelectedIds((prev) => {
            const visible = new Set(items.map((i) => i.id));
            const next = new Set();
            let changed = false;
            prev.forEach((id) => {
                if (visible.has(id)) next.add(id); else changed = true;
            });
            return changed ? next : prev;
        });
    }, [items]);

    const runDelete = useCallback(async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            if (confirmDelete.kind === "bulk") {
                const res = await api.post("/incidents/bulk-delete", { ids: confirmDelete.ids });
                const { deleted_count = 0, attachments_deleted = 0, missing_ids = [] } = res.data || {};
                toast.success(
                    `Deleted ${deleted_count} incident${deleted_count === 1 ? "" : "s"}` +
                    (attachments_deleted ? ` and ${attachments_deleted} attachment${attachments_deleted === 1 ? "" : "s"}` : "") +
                    (missing_ids.length ? ` — ${missing_ids.length} already gone` : "") + ".",
                );
            } else {
                await api.delete(`/incidents/${confirmDelete.id}`);
                toast.success("Incident deleted.");
            }
            setConfirmDelete(null);
            clearSelection();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    }, [confirmDelete, load, clearSelection]);

    // Sync `status` param → URL so KPI-tile deep-link (`?status=open`)
    // is preserved on reload.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (filters.status === "any") next.delete("status");
        else next.set("status", filters.status);
        setSearchParams(next, { replace: true });
    }, [filters.status]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!allowed) {
        return (
            <div className="asl-card p-8 text-center" data-testid="global-incidents-forbidden">
                <h2 className="text-lg font-semibold asl-navy-text">Restricted</h2>
                <p className="text-sm asl-text-2 mt-2">
                    The global incident directory is available to HR and Admin roles only.
                    Managers can view their module's incidents at <code>/m/{"{code}"}/incidents</code>.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="global-incidents-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-sm asl-text-2">
                        Every module's incidents in one place. HR + Admin only. Manager rows are read-only from here — status changes still route through HR.
                    </p>
                </div>
                <Button
                    data-testid="global-incidents-create-btn"
                    onClick={() => setCreateOpen(true)}
                    className="asl-btn-primary"
                >
                    + New incident
                </Button>
            </div>

            <FilterBar
                filters={filters}
                setFilters={setFilters}
                modules={modules || []}
            />

            {isAdmin && selectedIds.size > 0 && (
                <div
                    className="asl-card p-3 flex items-center justify-between gap-3"
                    style={{ borderLeft: "3px solid var(--asl-crimson, #A8324A)" }}
                    data-testid="global-incidents-bulk-toolbar"
                >
                    <div className="text-sm asl-text-1">
                        <span
                            className="font-semibold asl-navy-text"
                            data-testid="global-incidents-bulk-count"
                        >{selectedIds.size}</span>
                        {" "}selected
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={clearSelection}
                            data-testid="global-incidents-bulk-clear"
                        >
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            className="bg-[color:var(--asl-crimson,#A8324A)] text-white hover:opacity-90"
                            onClick={() => setConfirmDelete({
                                kind: "bulk",
                                ids: Array.from(selectedIds),
                            })}
                            data-testid="global-incidents-bulk-delete-btn"
                        >
                            Delete selected
                        </Button>
                    </div>
                </div>
            )}

            <div className="asl-card">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="global-incidents-table">
                        <thead>
                            <tr className="text-left asl-thead">
                                {isAdmin && (
                                    <th className="px-4 py-3 w-10">
                                        <Checkbox
                                            checked={items.length > 0 && selectedIds.size === items.length}
                                            onCheckedChange={(v) => toggleSelectAll(!!v)}
                                            data-testid="global-incidents-select-all"
                                            aria-label="Select all incidents"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3 w-40">Date</th>
                                <th className="px-4 py-3 w-20">Module</th>
                                <th className="px-4 py-3 w-24">Red flag</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3 w-28">Severity</th>
                                <th className="px-4 py-3">Employees</th>
                                <th className="px-4 py-3">Location</th>
                                <th className="px-4 py-3 w-40">Status</th>
                                <th className="px-4 py-3 w-40">Reporter</th>
                                {isAdmin && <th className="px-4 py-3 w-16 text-right">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={isAdmin ? 11 : 9} className="px-4 py-6 asl-text-2 text-center">
                                        Loading incidents…
                                    </td>
                                </tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr>
                                    <td colSpan={isAdmin ? 11 : 9} className="px-4 py-6 asl-text-2 text-center">
                                        No incidents match the current filters.
                                    </td>
                                </tr>
                            )}
                            {!loading && items.map((it) => (
                                <GlobalRow
                                    key={it.id}
                                    it={it}
                                    onOpen={() => setDetailId(it.id)}
                                    isAdmin={isAdmin}
                                    selected={selectedIds.has(it.id)}
                                    onToggleSelect={() => toggleSelect(it.id)}
                                    onDeleteRow={() => setConfirmDelete({
                                        kind: "single",
                                        id: it.id,
                                        label: TYPE_LABELS[it.incident_type] || it.incident_type,
                                    })}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isAdmin && (
                <Dialog
                    open={!!confirmDelete}
                    onOpenChange={(v) => !v && !deleting && setConfirmDelete(null)}
                >
                    <DialogContent data-testid="global-incidents-delete-dialog">
                        <DialogHeader>
                            <DialogTitle>
                                {confirmDelete?.kind === "bulk"
                                    ? `Delete ${confirmDelete.ids?.length || 0} incident${(confirmDelete?.ids?.length || 0) === 1 ? "" : "s"}?`
                                    : "Delete incident?"}
                            </DialogTitle>
                            <DialogDescription>
                                Attached files will also be permanently removed. This cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                                data-testid="global-incidents-delete-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-[color:var(--asl-crimson,#A8324A)] text-white hover:opacity-90"
                                onClick={runDelete}
                                disabled={deleting}
                                data-testid="global-incidents-delete-confirm"
                            >
                                {deleting ? "Deleting…" : "Delete permanently"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {createOpen && (
                <CreateIncidentDialog
                    moduleId={createModuleId || (modules?.[0]?.id ?? null)}
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    onCreated={() => { setCreateOpen(false); load(); }}
                    modulesForPicker={modules || []}
                    onPickModule={setCreateModuleId}
                />
            )}
            {detailId && (
                <DetailDrawer
                    incidentId={detailId}
                    open={!!detailId}
                    onOpenChange={(v) => !v && setDetailId(null)}
                    onChanged={load}
                    user={user}
                />
            )}
        </div>
    );
}

function FilterBar({ filters, setFilters, modules }) {
    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
    return (
        <div className="asl-card p-4 flex flex-wrap items-center gap-3" data-testid="global-incidents-filters">
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Module</Label>
                <Select value={filters.module_code} onValueChange={(v) => set("module_code", v)}>
                    <SelectTrigger className="w-32" data-testid="global-incidents-filter-module">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">All modules</SelectItem>
                        {modules.map((m) => (
                            <SelectItem key={m.id} value={(m.code || "").toLowerCase()}>{m.code}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Status</Label>
                <Select value={filters.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="w-44" data-testid="global-incidents-filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any status</SelectItem>
                        {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Severity</Label>
                <Select value={filters.severity} onValueChange={(v) => set("severity", v)}>
                    <SelectTrigger className="w-36" data-testid="global-incidents-filter-severity">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any severity</SelectItem>
                        {SEVERITIES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Type</Label>
                <Select value={filters.incident_type} onValueChange={(v) => set("incident_type", v)}>
                    <SelectTrigger className="w-56" data-testid="global-incidents-filter-type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any type</SelectItem>
                        {Object.entries(TYPE_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <Input
                className="w-56"
                placeholder="Search descriptions…"
                value={filters.q}
                onChange={(e) => set("q", e.target.value)}
                data-testid="global-incidents-filter-search"
            />
            <label
                className="ml-2 inline-flex items-center gap-2 text-sm asl-text-1 cursor-pointer"
                data-testid="global-incidents-filter-redflag-label"
            >
                <Checkbox
                    checked={filters.red_flag_only}
                    onCheckedChange={(v) => set("red_flag_only", !!v)}
                    data-testid="global-incidents-filter-redflag"
                />
                Red flags only
            </label>
        </div>
    );
}

function GlobalRow({ it, onOpen, isAdmin, selected, onToggleSelect, onDeleteRow }) {
    const sev = SEVERITY_TONE[it.severity] || SEVERITY_TONE.low;
    const st = STATUS_TONE[it.status] || STATUS_TONE.open;
    return (
        <tr
            className="cursor-pointer hover:bg-black/[0.03] transition-colors"
            style={{
                borderTop: "1px solid var(--asl-divider, rgba(0,0,0,0.08))",
                borderLeft: it.red_flag ? "3px solid var(--asl-crimson, #A8324A)" : "3px solid transparent",
            }}
            onClick={onOpen}
            data-testid={`global-incidents-row-${it.id}`}
        >
            {isAdmin && (
                <td
                    className="px-4 py-3 w-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Checkbox
                        checked={!!selected}
                        onCheckedChange={() => onToggleSelect?.()}
                        data-testid={`global-incidents-select-${it.id}`}
                        aria-label="Select incident"
                    />
                </td>
            )}
            <td className="px-4 py-3 whitespace-nowrap asl-text-1">{fmtDate(it.date_time)}</td>
            <td className="px-4 py-3"><ModuleCodeChip code={it.module_code} /></td>
            <td className="px-4 py-3">
                {it.red_flag ? (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide"
                        style={{
                            background: "rgba(168, 50, 74, 0.14)",
                            color: "var(--asl-crimson, #A8324A)",
                            border: "1px solid rgba(168, 50, 74, 0.28)",
                        }}
                        data-testid={`global-incident-red-flag-chip-${it.id}`}
                    >
                        Red flag
                    </span>
                ) : (
                    <span className="text-xs asl-text-3">—</span>
                )}
            </td>
            <td className="px-4 py-3 asl-text-1">{TYPE_LABELS[it.incident_type] || it.incident_type}</td>
            <td className="px-4 py-3">
                <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase"
                    style={{ background: sev.bg, color: sev.fg }}
                >
                    {it.severity}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                    {(it.employees || []).slice(0, 3).map((e) => (
                        <span key={e.id} className="inline-flex items-center gap-1.5 text-xs asl-text-1">
                            <ModuleIdChip value={e.module_employee_id} size="sm" />
                            <span className="truncate max-w-[8rem]">{e.full_name}</span>
                        </span>
                    ))}
                    {(it.employees || []).length > 3 && (
                        <span className="text-xs asl-text-3">
                            +{(it.employees || []).length - 3}
                        </span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3 asl-text-1">
                <div className="truncate max-w-xs" title={it.location}>{it.location || "—"}</div>
            </td>
            <td className="px-4 py-3">
                <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold"
                    style={{ background: st.bg, color: st.fg }}
                >
                    {STATUSES.find((s) => s.value === it.status)?.label || it.status}
                </span>
            </td>
            <td className="px-4 py-3 asl-text-2 text-xs">
                {it.reporter_by_name || it.reporter_email || "—"}
            </td>
            {isAdmin && (
                <td
                    className="px-4 py-3 text-right w-16"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={onDeleteRow}
                        className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                        style={{ color: "var(--asl-crimson, #A8324A)" }}
                        title="Delete incident"
                        data-testid={`global-incidents-delete-row-${it.id}`}
                    >
                        Delete
                    </button>
                </td>
            )}
        </tr>
    );
}
